import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { buildOnlineField, formatGeometry } from '/@/core/fields';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useZones } from '/@/hooks/zones';
import { setField } from '/@/store/crop-monitoring/actions';
import { createFieldFromAPI } from '/@/store/fields/apiProviders';
import { getSelectedFeature } from '/@/store/nav/selectors';
import { getAppInstance, getSessionCountries, getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { isMultiPolygon } from '/@/utils';
import { reverse } from '/@/utils/router';

export const useCreateField = ({ setError, setNewField }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const countries = useSelector(getSessionCountries);
    const country = useSelector(getSessionCountry);
    const zonesQuery = useZones({});
    const appInstance = useSelector(getAppInstance);
    const selectedFeature = useSelector(getSelectedFeature);
    const ownNodeQuery = useOwnUserNode();

    return useCallback(
        async ({ newField }) => {
            const { field: parsedField, error } = await buildOnlineField({
                payload: { ...newField },
                zones: zonesQuery.data,
                sessionCountry: country,
                sessionCountries: countries,
            });

            if (error) {
                setError(error);
                if (parsedField?.country) setNewField((f) => ({ ...f, country: parsedField.country }));
                throw new Error(error);
            }
            if (!parsedField) return;

            // Normal creation

            const { geometry } = parsedField;
            const { selectedFeature: innerSelectedFeature, crop } =
                (!Array.isArray(selectedFeature) && selectedFeature) || {};
            const field = await createFieldFromAPI({
                name: parsedField.name,
                geometry: isMultiPolygon(geometry) ? geometry.map(formatGeometry) : formatGeometry(geometry),
                appInstance: appInstance.id,
                altitude: parsedField?.altitude,
                area: parsedField?.area,
                country: country.code,
                userNode: newField?.userNodeId,
            });

            mpTrackEvent('Field created', {
                area_hectares: parsedField?.area,
                field_id: field.id,
                creation_method: newField?.source,
                create_own_user_field: newField?.userNodeId === ownNodeQuery.data?.id,
            });

            const fieldId = field.id;

            if (innerSelectedFeature === 'carbonCalculator') {
                navigate(reverse('CarbonCalculator', { params: { fieldId } }));
                return fieldId;
            }

            if (innerSelectedFeature === 'newNitrogenRecommendationForm') {
                dispatch(setField(fieldId));
                navigate(reverse('nitrogenRecommendation.NitrogenRecommendationCreate', { params: { fieldId } }));
                return fieldId;
            }

            return fieldId;
        },
        [
            appInstance.id,
            countries,
            country,
            dispatch,
            navigate,
            selectedFeature,
            setError,
            setNewField,
            zonesQuery.data,
            ownNodeQuery.data?.id,
        ]
    );
};
